import { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BookOpenCheck,
  CheckCircle2,
  Globe2,
  Landmark,
  ShieldCheck,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  globalPresenceGallery,
  globalPresenceHighlights,
  lineageMoments,
  publicLeadershipMoments,
} from "../data/globalPresence";
import PageBanner from "../components/PageBanner";
import { useRegionalExperience } from "../hooks/useRegionalExperience";

const principleIcons = [BookOpenCheck, Globe2, ShieldCheck];

const EMPTY_CONFIG = {};

const configuredImage = (item) => item?.image || item?.imageUrl || item?.src;

const configuredAlt = (item, fallback) =>
  item?.alt || item?.imageAlt || item?.title || item?.label || fallback;

const dedupeByImage = (items) => {
  const seen = new Set();

  return items.filter((item) => {
    const image = configuredImage(item);
    if (!image || seen.has(image)) return false;
    seen.add(image);
    return true;
  });
};

const asPresenceHighlight = (event, index) => {
  const fallback = globalPresenceHighlights[index] || globalPresenceHighlights[0];
  const image = configuredImage(event) || fallback.image;

  return {
    eyebrow: event?.eyebrow || (event?.year ? `${event.year} public presence` : fallback.eyebrow),
    place: event?.place || fallback.place,
    title: event?.title || fallback.title,
    text: event?.text || event?.description || fallback.text,
    image,
    alt: configuredAlt(event, fallback.alt),
    objectPosition: event?.objectPosition || fallback.objectPosition || "center",
  };
};

const asGalleryImage = (item, fallbacks = {}) => ({
  category: item?.category || item?.eyebrow || fallbacks.category || "Mission gallery",
  label: item?.label || item?.caption || item?.title || fallbacks.label || "Mission field image",
  image: configuredImage(item),
  imageMobile: item?.srcMobile || item?.imageMobile || "",
  alt: configuredAlt(item, fallbacks.alt || "AGM mission field image"),
  objectPosition: item?.objectPosition || "center",
});

const SectionIntro = ({ eyebrow, title, description, align = "left" }) => (
  <div className={align === "center" ? "mx-auto max-w-3xl text-center" : "max-w-3xl"}>
    <p className="mb-3 text-xs font-bold uppercase tracking-[0.28em] text-[#9d1f1c]">{eyebrow}</p>
    <h2 className="text-3xl font-semibold leading-tight text-[#241511] md:text-5xl">{title}</h2>
    {description && (
      <p className="mt-5 text-base leading-8 text-[#5f4a3e] md:text-lg">{description}</p>
    )}
  </div>
);

const GlobalPresence = () => {
  const { t } = useTranslation();
  const { siteConfig, region } = useRegionalExperience();
  const usaPresence = siteConfig.usaPresence || EMPTY_CONFIG;
  const swamiji = siteConfig.swamiji || EMPTY_CONFIG;
  const vedant = siteConfig.vedant || EMPTY_CONFIG;
  const brandShortName = siteConfig.brand?.shortName || "AGM";

  const principles = useMemo(
    () => [
      {
        icon: principleIcons[0],
        title: t("globalPresence.principles.lineage.title"),
        text: t("globalPresence.principles.lineage.text"),
      },
      {
        icon: principleIcons[1],
        title: t("globalPresence.principles.globalResponsibility.title"),
        text: t("globalPresence.principles.globalResponsibility.text"),
      },
      {
        icon: principleIcons[2],
        title: t("globalPresence.principles.stature.title"),
        text: t("globalPresence.principles.stature.text"),
      },
    ],
    [t]
  );

  // Prefer CMS-managed Home Presence content (edited in the CMS "Home Presence" tab);
  // fall back to usaPresence-derived events, then to the static seed data.
  const cmsGlobalPresence = siteConfig.homePresence?.global || EMPTY_CONFIG;

  const presenceHighlights = useMemo(() => {
    const cmsHighlights = Array.isArray(cmsGlobalPresence.highlights)
      ? cmsGlobalPresence.highlights
      : [];
    if (cmsHighlights.length) return cmsHighlights;
    const configuredEvents = Array.isArray(usaPresence.events) ? usaPresence.events : [];
    const mappedEvents = configuredEvents.map(asPresenceHighlight);

    return mappedEvents.length ? mappedEvents : globalPresenceHighlights;
  }, [cmsGlobalPresence.highlights, usaPresence.events]);

  const presenceLineage = useMemo(() => {
    const cmsLineage = Array.isArray(cmsGlobalPresence.lineage) ? cmsGlobalPresence.lineage : [];
    return cmsLineage.length ? cmsLineage : lineageMoments;
  }, [cmsGlobalPresence.lineage]);

  const heroImages = useMemo(
    () =>
      dedupeByImage([
        {
          ...publicLeadershipMoments[0],
          image: publicLeadershipMoments[0].featureImage,
          alt: publicLeadershipMoments[0].featureAlt,
          objectPosition: "center",
        },
        presenceHighlights[0],
        lineageMoments[0],
        {
          eyebrow: swamiji.eyebrow || vedant.title || t("globalPresence.hero.livingGuidance"),
          title: swamiji.title || vedant.title || globalPresenceHighlights[2].title,
          image: swamiji.image || vedant.image || globalPresenceHighlights[2].image,
          alt: swamiji.title || configuredAlt(vedant, globalPresenceHighlights[2].alt),
          aspect: "4 / 3",
          imageFit: "cover",
          objectPosition: "center",
        },
      ]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [presenceHighlights, swamiji, vedant, t]
  );

  const principleCards = useMemo(() => {
    const configuredPrinciples = Array.isArray(vedant.principles) ? vedant.principles : [];

    return principles.map((item, index) => ({
      ...item,
      text: configuredPrinciples[index] || item.text,
    }));
  }, [vedant.principles, principles]);

  const visualGallery = useMemo(() => {
    const galleryFallbacks = {
      category: t("globalPresence.gallery.defaultCategory"),
      label: t("globalPresence.gallery.defaultLabel"),
      alt: t("globalPresence.gallery.defaultAlt"),
    };
    const configuredGallery = Array.isArray(siteConfig.gallery)
      ? siteConfig.gallery.map((item) => asGalleryImage(item, galleryFallbacks))
      : [];

    return dedupeByImage([
      ...globalPresenceGallery.slice(0, 4),
      ...configuredGallery,
      ...globalPresenceGallery.slice(4),
    ]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteConfig.gallery, t]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="overflow-x-clip bg-[#fffaf5] text-[#241511]">
      <PageBanner
        {...(siteConfig.globalPresencePage?.banner || {})}
        rightSlot={
          <div className="grid content-center items-start gap-4 sm:grid-cols-2">
            {heroImages.map((image) => {
              const imageFit = image.featureImageFit || image.imageFit || "contain";
              const imageAspect = image.featureAspect || image.aspect || "4 / 3";
              const imagePosition = image.featureObjectPosition || image.objectPosition || "center";

              return (
                <figure
                  key={image.image}
                  className="group overflow-hidden rounded-lg border border-white/16 bg-white/10 shadow-2xl shadow-black/20"
                >
                  <div className="overflow-hidden bg-white/8" style={{ aspectRatio: imageAspect }}>
                    <img
                      src={image.image}
                      alt={image.alt}
                      className={`h-full w-full transition duration-700 group-hover:scale-105 ${
                        imageFit === "contain" ? "object-contain" : "object-cover"
                      }`}
                      style={{ objectPosition: imagePosition }}
                    />
                  </div>
                  <figcaption className="border-t border-white/10 p-4">
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#f5c45e]">
                      {image.eyebrow}
                    </p>
                    <p className="mt-2 text-sm font-semibold leading-6 text-white/86">
                      {image.title}
                    </p>
                  </figcaption>
                </figure>
              );
            })}
          </div>
        }
      />

      <section className="border-b border-[#ead9cd] bg-white py-10">
        <div className="container mx-auto grid gap-4 px-4 sm:px-6 md:grid-cols-3 lg:px-8">
          {principleCards.map(({ icon: Icon, title, text }) => (
            <article key={title} className="rounded-lg border border-[#ead9cd] bg-[#fffaf5] p-6">
              <Icon className="h-7 w-7 text-[#9d1f1c]" />
              <h2 className="mt-5 text-xl font-semibold leading-tight text-[#241511]">{title}</h2>
              <p className="mt-3 text-sm leading-7 text-[#5f4a3e]">{text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
            <SectionIntro
              eyebrow={t("globalPresence.usaPresence.eyebrow")}
              title={
                usaPresence.title ||
                t("globalPresence.usaPresence.title")
              }
              description={
                usaPresence.description ||
                t("globalPresence.usaPresence.description")
              }
            />
            <Link
              to={region.donationPath}
              className="inline-flex w-fit items-center gap-3 rounded-md bg-[#9d1f1c] px-6 py-4 text-sm font-bold uppercase tracking-[0.14em] text-white transition hover:bg-[#7f1916]"
            >
              {region.paymentLabel}
              <ArrowRight size={18} />
            </Link>
          </div>

          <div className="mt-12 grid gap-6 lg:grid-cols-4">
            {presenceHighlights.map((item) => (
              <article
                key={item.title}
                className="group overflow-hidden rounded-lg border border-[#ead9cd] bg-white shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-xl"
              >
                <div className="aspect-[4/3] overflow-hidden bg-[#f8efe5]">
                  <picture>
                    {item.imageMobile ? (
                      <source srcSet={item.imageMobile} media="(max-width: 767px)" />
                    ) : null}
                    <img
                      src={item.image}
                      alt={item.alt}
                      className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
                      style={{ objectPosition: item.objectPosition || "center" }}
                      loading="lazy"
                    />
                  </picture>
                </div>
                <div className="p-6">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#9d1f1c]">
                    {item.eyebrow}
                  </p>
                  <p className="mt-2 text-xs font-semibold uppercase tracking-[0.16em] text-[#8b7160]">
                    {item.place}
                  </p>
                  <h3 className="mt-4 text-2xl font-semibold leading-tight text-[#241511]">
                    {item.title}
                  </h3>
                  <p className="mt-4 text-sm leading-7 text-[#5f4a3e]">{item.text}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#f8efe5] py-20 md:py-28">
        <div className="container mx-auto grid gap-12 px-4 sm:px-6 lg:grid-cols-[0.36fr_0.64fr] lg:px-8">
          <div className="lg:sticky lg:top-28 lg:self-start">
            <Landmark className="h-9 w-9 text-[#9d1f1c]" />
            <SectionIntro
              eyebrow={t("globalPresence.lineage.eyebrow")}
              title={t("globalPresence.lineage.title")}
              description={t("globalPresence.lineage.description")}
            />
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            {presenceLineage.map((item) => (
              <article
                key={item.title}
                className="overflow-hidden rounded-lg border border-[#e1caba] bg-white shadow-sm"
              >
                <div className="aspect-[16/11] overflow-hidden bg-[#f3e2d4]">
                  <picture>
                    {item.imageMobile ? (
                      <source srcSet={item.imageMobile} media="(max-width: 767px)" />
                    ) : null}
                    <img
                      src={item.image}
                      alt={item.alt}
                      className="h-full w-full object-cover"
                      style={{ objectPosition: item.objectPosition || "center" }}
                      loading="lazy"
                    />
                  </picture>
                </div>
                <div className="p-6">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#9d1f1c]">
                    {item.eyebrow}
                  </p>
                  <h3 className="mt-4 text-2xl font-semibold leading-tight text-[#241511]">
                    {item.title}
                  </h3>
                  <p className="mt-4 text-sm leading-7 text-[#5f4a3e]">{item.text}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-20 md:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <SectionIntro
            eyebrow={t("globalPresence.gallery.eyebrow")}
            title={t("globalPresence.gallery.title")}
            description={t("globalPresence.gallery.description")}
            align="center"
          />

          <div className="mt-12 grid auto-rows-[260px] gap-4 md:grid-cols-3 lg:grid-cols-4">
            {visualGallery.map((item, index) => {
              const featured = index === 0 || index === 6 || index === 10;
              return (
                <figure
                  key={`${item.category}-${item.label}`}
                  className={`group relative overflow-hidden rounded-lg border border-[#ead9cd] bg-[#f8efe5] ${
                    featured ? "md:col-span-2 md:row-span-2" : ""
                  }`}
                >
                  <picture>
                    {item.imageMobile ? (
                      <source srcSet={item.imageMobile} media="(max-width: 767px)" />
                    ) : null}
                    <img
                      src={item.image}
                      alt={item.alt}
                      className="absolute inset-0 h-full w-full object-cover transition duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                  </picture>
                  <div className="absolute inset-0 bg-gradient-to-t from-[#241511]/86 via-[#241511]/16 to-transparent" />
                  <figcaption className="absolute inset-x-0 bottom-0 p-5 text-white">
                    <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#f5c45e]">
                      {item.category}
                    </p>
                    <p className="mt-2 text-base font-semibold leading-snug">{item.label}</p>
                  </figcaption>
                </figure>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="overflow-hidden rounded-lg bg-[linear-gradient(135deg,#2a120d_0%,#4b2418_54%,#7f1916_100%)] text-white">
            <div className="grid gap-8 p-8 md:p-12 lg:grid-cols-[0.62fr_0.38fr] lg:items-center">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#f5c45e]">
                  {t("globalPresence.cta.eyebrow")}
                </p>
                <h2 className="mt-4 text-3xl font-semibold leading-tight md:text-5xl">
                  {t("globalPresence.cta.heading")}
                </h2>
                <p className="mt-5 text-base leading-8 text-white/76">
                  {t("globalPresence.cta.description")}
                </p>
              </div>
              <div className="grid gap-3">
                {[
                  t("globalPresence.cta.bullets.guide", { regionLabel: region.label, currency: region.currency }),
                  t("globalPresence.cta.bullets.connect"),
                  t("globalPresence.cta.bullets.keep", { brandShortName }),
                ].map((item) => (
                  <div key={item} className="flex gap-3 border border-white/14 bg-white/8 p-4">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#f5c45e]" />
                    <p className="text-sm leading-7 text-white/78">{item}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid border-t border-white/12 md:grid-cols-3">
              {[
                [
                  swamiji.secondaryCtaLabel || t("globalPresence.ctaLinks.learnVedant.label"),
                  swamiji.secondaryCtaHref || "/vedant",
                  vedant.title || t("globalPresence.ctaLinks.learnVedant.text", { brandShortName }),
                ],
                [
                  t("globalPresence.ctaLinks.programs.label"),
                  "/programs",
                  t("globalPresence.ctaLinks.programs.text"),
                ],
                [region.paymentLabel, region.donationPath, t("globalPresence.ctaLinks.give.text")],
              ].map(([label, href, text]) => (
                <Link
                  key={label}
                  to={href}
                  className="group flex items-center justify-between gap-4 border-white/12 p-6 transition hover:bg-white/10 md:border-r"
                >
                  <span>
                    <span className="block text-lg font-semibold">{label}</span>
                    <span className="mt-1 block text-sm text-white/62">{text}</span>
                  </span>
                  <ArrowRight className="h-5 w-5 shrink-0 text-[#f5c45e] transition group-hover:translate-x-1" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default GlobalPresence;
